<template>
  <div>
    <!-- The level layer beneath the planview room layer -->
    <GeojsonLayer
      v-if="layerRoomLevelBeneathSource"
      :before="before"
      :layer="layerRoomLevelBeneathStyle"
      :source="layerRoomLevelBeneathSource"
      :sourceId="layerRoomLevelBeneathSourceId"
      @layerClicked="handleClickLevel"
      @layerRightClicked="handleRightClick"
      @layerMouseEnter="setCursor('pointer')"
      @layerMouseLeave="handleMouseLeave"
      @layerMouseMove="handleMouseMoveLevel"
    />
    <!-- The planview room layer -->
    <GeojsonLayer
      v-if="layerRoomSource"
      :before="before"
      :layer="layerRoomStyle"
      :source="layerRoomSource"
      :sourceId="layerRoomSourceId"
      @layerClicked="handleClick"
      @layerMouseEnter="setCursor('pointer')"
      @layerMouseLeave="handleMouseLeave"
      @layerMouseMove="updateHoverId"
    />
    <GeojsonLayer
      v-if="layerRoomSource"
      :before="before"
      :layer="layerRoomOutlineStyle"
      :layerId="layerIdRoomOutline"
      :source="layerRoomSource"
      :sourceId="layerRoomSourceId"
    />
    <div v-show="false">
      <MapContextMenu
        v-if="hasPoiCreatorAccess"
        ref="rightClickButtons"
        :buttons="contextMenuButtons"
        @clickCreatePOI="handleClickOnCreatePOIButton"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import { useGeneralStore } from '@/pinia'
import { mapState as mapStatePinia } from 'pinia'
import mapboxgl from 'mapbox-gl'
import GeojsonLayer from '@/components/map/mapbox-layers/GeojsonLayer.vue'
import MapContextMenu from '@/components/map/popups/MapContextMenu.vue'
import { CHANGE_ROOM, RESET_PLANVIEW_SELECTIONS } from '@/store/action-types'
import { SET_CLICKED_ITEM_TYPE } from '@/store/mutation-types'
import { useCurrentFeatureFilters } from '@/composables/useFilters'
import { useHoverStore } from '@/pinia/hover'

export default {
  name: 'LayerRoom',
  components: {
    GeojsonLayer,
    MapContextMenu
  },
  props: {
    before: {
      type: String,
      required: true
    },
    layerIdRoomLevelBeneath: {
      type: String,
      required: true
    },
    layerIdRoomOutline: {
      type: String,
      required: true
    },
    layerIdRoom: {
      type: String,
      required: true
    },
    layerIdRoomText: {
      type: String,
      required: true
    }
  },
  setup() {
    const { setCurrentOverlayModalPinia, setRightClickCoordinate, setShowOverlayModalPinia } =
      useGeneralStore()
    const { hoveredItems } = useHoverStore()
    const { useFilters } = useCurrentFeatureFilters()
    return {
      setCurrentOverlayModalPinia,
      setRightClickCoordinate,
      setShowOverlayModalPinia,
      hoveredItems,
      useFilters
    }
  },
  data() {
    return {
      layerRoomLevelBeneathSourceId: 'source-room-level-beneath',
      layerRoomSourceId: 'source-room',
      hoveredRoomId: null,
      rightClickContextMenu: new mapboxgl.Popup({
        anchor: 'top-left',
        className: 'layer-room-map-context-menu',
        closeButton: false
      }),
      rightClickCoordinate: []
    }
  },
  computed: {
    ...mapGetters({
      hasPoiCreatorAccess: 'hasPoiCreatorAccess',
      isDrawPointActive: 'isDrawPointActive',
      isMeasurementActive: 'isMeasurementActive',
      isModelviewShowing: 'isModelviewShowing',
      isPlanviewShowing: 'isPlanviewShowing',
      selectedLevelId: 'selectedLevelId',
      selectedRoomId: 'selectedRoomId'
    }),
    ...mapState({
      levelsForSelectedBuilding: (state) => state.levelsForSelectedBuilding,
      selectedLevel: (state) => state.selectedLevel,
      roomsForSelectedLevel: (state) => state.roomsForSelectedLevel,
      mountedMapLayerIds: (state) => state.mountedMapLayerIds,
      clickItemType: (state) => state.clickItemType
    }),
    ...mapStatePinia(useGeneralStore, [
      'currentOverlayModalPinia',
      'filteredDrawings',
      'filteredOrthophotos',
      'selectedAttachment',
      'selectedTicket',
      'showOverlayModalPinia'
    ]),

    contextMenuButtons() {
      const buttons = []
      buttons.push({
        title: this.$t('general__text__create-new-asset'),
        emit: 'clickCreatePOI',
        icon: 'IconPlusMedium'
      })
      return buttons
    },
    filters() {
      return useCurrentFeatureFilters()
    },
    submittedRoomTypes() {
      if (!this.filters.value) return undefined
      if (!this.filters.value.submitted) return undefined
      if (this.filters.value.$id === 'building_score') return undefined
      if (this.filters.value.submitted?.roomTypes?.length === 0) return undefined
      const types = this.filters.value.submitted?.roomTypes
        ?.map((roomType) => roomType.coclass)
        .filter(Boolean)
      return types && types.length > 0 ? types : undefined
    },
    isDrawingLayerMounted() {
      return (
        this.mountedMapLayerIds.some((layer) => layer.startsWith('layer-drawing-floor')) &&
        this.filteredDrawings.some((drawing) => drawing.visible)
      )
    },
    isOrthophotoLayerMounted() {
      return (
        this.mountedMapLayerIds.some((layer) => layer.startsWith('layer-orthophoto-floor')) &&
        this.filteredOrthophotos.some((orthophoto) => orthophoto.visible)
      )
    },
    layerRoomStyle() {
      return {
        id: this.layerIdRoom,
        source: this.layerRoomSourceId,
        type: 'fill',
        paint: this.layerRoomPaint
      }
    },
    layerRoomOutlineStyle() {
      return {
        id: this.layerIdRoomOutline,
        source: this.layerRoomSourceId,
        type: 'line',
        layout: {
          visibility:
            !this.isDrawingLayerMounted && !this.isOrthophotoLayerMounted ? 'visible' : 'none'
        },
        paint: {
          'line-color': '#16191C',
          'line-width': 1
        }
      }
    },
    layerRoomLevelBeneathStyle() {
      const fillColor = [
        'case',
        ['==', this.selectedAttachmentEntityId ?? null, ['get', 'id']],
        '#FADB14',
        ['==', this.selectedTicketEntityId ?? null, ['get', 'id']],
        '#FADB14',
        '#1D234F'
      ]
      return {
        id: this.layerIdRoomLevelBeneath,
        source: this.layerRoomLevelBeneathSourceId,
        type: 'fill',
        layout: {
          visibility: this.layerRoomLevelVisibility
        },
        paint: {
          'fill-color': fillColor,
          'fill-opacity': !this.isDrawingLayerMounted && !this.isOrthophotoLayerMounted ? 1 : 0
        }
      }
    },
    layerRoomSource() {
      if (!this.roomsForSelectedLevel) return null
      const newSource = {
        type: 'geojson',
        promoteId: 'id',
        data: {
          id: this.layerRoomSourceId,
          type: 'FeatureCollection',
          features: this.roomsForSelectedLevel.data.features
        }
      }
      return newSource
    },
    layerRoomLevelBeneathSource() {
      if (!this.levelsForSelectedBuilding) return null
      const newSource = {
        type: 'geojson',
        promoteId: 'id',
        data: {
          id: this.layerRoomLevelBeneathSourceId,
          type: 'FeatureCollection',
          features: []
        }
      }
      const floor = this.levelsForSelectedBuilding.data.features.find(
        (el) => el.id === this.selectedLevelId
      )
      newSource.data.features.push(floor)
      return newSource
    },
    layerRoomLevelVisibility() {
      return this.isPlanviewShowing ? 'visible' : 'none'
    },
    layerRoomPaint() {
      if (this.submittedRoomTypes) {
        const fillColor = [
          'case',
          ['==', this.selectedAttachmentEntityId ?? null, ['get', 'id']],
          '#FADB14',
          ['==', this.selectedTicketEntityId ?? null, ['get', 'id']],
          '#FADB14',
          ['==', this.selectedRoomId ?? null, ['get', 'id']],
          ['coalesce', ['feature-state', 'color'], '#80b5b4'],
          [
            ('all',
            ['==', this.hoveredRoomId ?? null, ['get', 'id']],
            ['in', ['get', 'classCode'], ['literal', this.submittedRoomTypes]])
          ],
          '#b4d6d6',
          ['!', ['in', ['get', 'classCode'], ['literal', this.submittedRoomTypes]]],
          '#E0E0E0',
          '#F9F9F9'
        ]
        const style = {
          'fill-color': fillColor
        }
        if (!this.isDrawingLayerMounted && !this.isOrthophotoLayerMounted) return style
        style['fill-outline-color'] = '#ffffff'
        style['fill-opacity'] = [
          'case',
          ['==', this.selectedRoomId, ['get', 'id']],
          0.7,
          ['==', this.hoveredRoomId, ['get', 'id']],
          0.7,
          0
        ]
        return style
      } else {
        const fillColor = [
          'case',
          ['==', this.selectedAttachmentEntityId, ['get', 'id']],
          '#FADB14',
          ['==', this.selectedTicketEntityId, ['get', 'id']],
          '#FADB14',
          ['==', this.selectedRoomId ?? null, ['get', 'id']],
          ['coalesce', ['feature-state', 'color'], '#80b5b4'],
          ['==', this.hoveredRoomId ?? null, ['get', 'id']],
          '#b4d6d6',
          ['coalesce', ['feature-state', 'color'], '#F8F8F8']
        ]
        const style = {
          'fill-color': fillColor,
          'fill-opacity': 1
        }
        if (!this.isDrawingLayerMounted && !this.isOrthophotoLayerMounted) return style
        style['fill-outline-color'] = '#ffffff'
        style['fill-opacity'] = [
          'case',
          ['==', this.selectedRoomId, ['get', 'id']],
          0.7,
          ['==', this.hoveredRoomId, ['get', 'id']],
          0.7,
          0
        ]
        return style
      }
    },
    selectedAttachmentEntityId() {
      return this.selectedAttachment?.entityId ? this.selectedAttachment.entityId : ''
    },
    selectedTicketEntityId() {
      return this.selectedTicket?.entityConnection?.length &&
        this.selectedTicket?.entityConnection[0].id
        ? this.selectedTicket.entityConnection[0].id
        : ''
    }
  },
  watch: {
    isPlanviewShowing(isShowing) {
      if (!isShowing) this.rightClickContextMenu.remove()
    },

    selectedLevel(newLevel) {
      if (newLevel) this.rightClickContextMenu.remove()
    },
    showOverlayModalPinia(newVal) {
      if (newVal && this.currentOverlayModalPinia === 'editPoi')
        if (this.rightClickContextMenu) this.rightClickContextMenu.remove()
    }
  },
  methods: {
    ...mapActions({
      commitRoom: CHANGE_ROOM,
      resetPlanviewSelections: RESET_PLANVIEW_SELECTIONS
    }),
    ...mapMutations({
      commitClick: SET_CLICKED_ITEM_TYPE
    }),
    handleClick(event) {
      if (this.isDrawPointActive || this.isMeasurementActive || !this.isFirstLayer(event)) {
        return
      }
      if (
        this.submittedRoomTypes &&
        !this.submittedRoomTypes.includes(event.features[0].properties.classCode)
      ) {
        return
      }
      this.commitRoom({ roomId: event.features[0].properties.id })
      this.commitClick({ type: 'room' })
    },
    handleClickLevel(event) {
      if (this.isDrawPointActive || this.isMeasurementActive || !this.isLevelFirstLayer(event))
        return
      this.commitClick({ type: 'level' })
      this.resetPlanviewSelections()
    },
    handleClickOnCreatePOIButton() {
      this.setRightClickCoordinate(this.rightClickCoordinate)
      this.setCurrentOverlayModalPinia('createAssets')
      this.setShowOverlayModalPinia(true)
    },
    handleMouseLeave(event) {
      if (this.isMeasurementActive) return
      if (!this.isLevelFirstLayer(event)) this.setCursor('')
      this.hoveredRoomId = null
    },
    handleMouseMoveLevel() {
      if (this.$store.map.getCanvas().style.cursor !== 'pointer') this.setCursor('pointer')
    },
    handleRightClick(event) {
      if (
        !this.hasPoiCreatorAccess ||
        !this.$refs.rightClickButtons ||
        !this.isPlanviewShowing ||
        this.isDrawPointActive
      )
        return
      this.rightClickCoordinate = [event.lngLat.lng, event.lngLat.lat]
      this.rightClickContextMenu
        .setLngLat(event.lngLat)
        .setDOMContent(this.$refs.rightClickButtons.$el)
        .addTo(this.$store.map)
    },
    isFirstLayer(event) {
      const renderedFeatures = this.$store.map.queryRenderedFeatures(event.point)
      if (!renderedFeatures.length) return false
      const isVisiblePoiHovered = renderedFeatures.findIndex(
        (feature) => feature.layer.id === 'layer-room-marker-poi'
      )
      const isVisibleZoneHovered = renderedFeatures.find((feature) =>
        feature.layer.id.startsWith('layer-level-zone')
      )
      if (isVisiblePoiHovered !== -1 || isVisibleZoneHovered) return false
      if (renderedFeatures[0].layer.id === 'colored-spaces-layer') return true
      return (
        renderedFeatures[0].layer.id === this.layerIdRoom ||
        (renderedFeatures[0].layer.id === this.layerIdRoomText &&
          renderedFeatures[1].layer.id === this.layerIdRoom)
      )
    },
    isLevelFirstLayer(event) {
      const renderedFeatures = this.$store.map.queryRenderedFeatures(event.point)
      if (!renderedFeatures.length) return false
      // Below is true if the layer level is the top layer or if it is the first layer below the room text layer
      return (
        this.layerRoomLevelBeneathSourceId === renderedFeatures[0].source ||
        (renderedFeatures.length > 1 &&
          this.layerRoomLevelBeneathSourceId === renderedFeatures[1].source &&
          this.layerIdRoomText === renderedFeatures[0].layer.id)
      )
    },
    setCursor(cursorType) {
      if (this.isMeasurementActive) return
      this.$store.map.getCanvas().style.cursor = cursorType
    },
    updateHoverId(event) {
      const features = event.features
      if (
        this.submittedRoomTypes &&
        !this.submittedRoomTypes.includes(event.features[0].properties.classCode)
      ) {
        this.setCursor('auto')
      }
      if (this.isMeasurementActive || !this.isFirstLayer(event) || !features || !features.length) {
        this.hoveredRoomId = null
        return
      }
      if (this.hoveredRoomId === features[0].properties.belongsToId) return
      this.hoveredRoomId = features[0].properties.belongsToId
    }
  }
}
</script>
<style lang="scss">
// Be aware that this css isn't scoped!!!
.layer-room-map-context-menu {
  .mapboxgl-popup-tip {
    display: none !important;
  }
}
</style>
